import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BadgeCheck,
  BookOpen,
  ChevronDown,
  CircleHelp,
  CreditCard,
  GraduationCap,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Ticket,
  UserPlus,
  Wallet,
  LayoutDashboard,
} from 'lucide-react';

const PAGE_BG = '#f4f7fc';

const steps = [
  {
    n: 1,
    title: 'ثبت‌نام یا ورود',
    desc: 'برای خرید و فعال‌سازی دوره باید وارد حساب شوید. در چند ثانیه ثبت‌نام کنید یا وارد شوید.',
    icon: UserPlus,
    anchor: '/register',
    anchorLabel: 'ثبت‌نام',
    anchorSecondary: { to: '/login', label: 'ورود' },
  },
  {
    n: 2,
    title: 'انتخاب دوره',
    desc: 'از کتابخانهٔ دوره‌ها، عنوان مدنظر را باز کنید، سرفصل و قیمت را ببینید و تصمیم بگیرید.',
    icon: GraduationCap,
    anchor: '/courses',
    anchorLabel: 'مشاهده دوره‌ها',
  },
  {
    n: 3,
    title: 'افزودن به سبد',
    desc: 'در صفحهٔ هر دوره روی «افزودن به سبد» بزنید. می‌توانید چند دوره را با هم در سبد داشته باشید.',
    icon: ShoppingCart,
    anchor: '/courses',
    anchorLabel: 'رفتن به دوره‌ها',
  },
  {
    n: 4,
    title: 'سبد خرید و جمع مبلغ',
    desc: 'در صفحهٔ سبد، جمع قبل و بعد از تخفیف دوره‌ها را می‌بینید و موجودی کیف پول خود را با «مبلغ قابل پرداخت» مقایسه کنید.',
    icon: ShoppingCart,
    anchor: '/cart',
    anchorLabel: 'سبد خرید',
  },
  {
    n: 5,
    title: 'کد تخفیف (اختیاری)',
    desc: 'کد تخفیف فقط در همین صفحهٔ سبد وارد می‌شود؛ ابتدا «اعمال کوپن» را بزنید تا پیش‌نمایش قیمت هر دوره به‌روز شود، سپس تسویه کنید.',
    icon: Ticket,
    anchor: '/cart',
    anchorLabel: 'سبد با کوپن',
  },
  {
    n: 6,
    title: 'افزایش موجودی کیف پول',
    desc: 'اگر اعتبار کافی نیست، از «درخواست افزایش موجودی» مبلغ، روش واریز و رسید را ثبت کنید. پس از تأیید مالی، کیف پول شارژ می‌شود.',
    icon: CreditCard,
    anchor: '/cart',
    anchorLabel: 'صفحهٔ سبد',
  },
  {
    n: 7,
    title: 'پرداخت و فعال‌سازی',
    desc: 'با زدن «پرداخت از کیف پول و فعال‌سازی»، مبلغ از کیف پول کسر می‌شود و دسترسی به محتوای دوره بلافاصله فعال می‌گردد.',
    icon: BadgeCheck,
    anchor: '/cart',
    anchorLabel: 'تسویه سبد',
  },
];

const faqItems = [
  {
    q: 'آیا می‌توانم بدون سبد خرید کنم؟',
    a: 'در بسیاری از دوره‌ها از خود صفحهٔ دوره هم گزینهٔ خرید و فعال‌سازی وجود دارد. مسیر پیشنهادی ما برای اعمال کوپن و خرید چندتایی، همان سبد خرید است. بسته به وضعیت پرداخت، ممکن است در بعضی مسیرها به درگاه بانکی هدایت شوید.',
  },
  {
    q: 'چرا بعد از زدن دکمهٔ پرداخت، خطای موجودی گرفتم؟',
    a: 'تسویه از طریق سبد با «موجودی کیف پول» انجام می‌شود. اگر جمع سبد بیشتر از اعتبار شما باشد، ابتدا باید درخواست افزایش موجودی ثبت شود یا موجودی کافی شود و دوباره تلاش کنید.',
  },
  {
    q: 'کد تخفیف را کجا بزنم؟',
    a: 'فقط در صفحهٔ سبد خرید، در بخش کد تخفیف؛ پس از «اعمال کوپن» قیمت‌ها به‌روز می‌شوند و هنگام تسویه اعمال می‌گردند.',
  },
  {
    q: 'دورهٔ رایگان چطور فعال می‌شود؟',
    a: 'برای دوره‌های رایگان معمولاً از همان صفحهٔ دوره بدون پرداخت، ثبت‌نام / فعال‌سازی انجام می‌شود.',
  },
  {
    q: 'بعد از پرداخت مشکل فنی دارم؛ با کی تماس بگیرم؟',
    a: 'از صفحهٔ تماس با ما یا شمارهٔ پشتیبانیٔ درج‌شده در سایت اقدام کنید تا کارشناسان راهنمایی‌تان کنند.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

type Step = (typeof steps)[number];

const PurchaseGuide: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen pb-16 pt-4 text-[#0f172a]" style={{ backgroundColor: PAGE_BG }} dir="rtl">
      <div className="mx-auto w-full max-w-[min(1180px,96vw)] px-3 sm:px-5 lg:px-6">
        <nav
          aria-label="مسیر دسترسی"
          className="mb-4 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-slate-500 sm:text-xs"
        >
          <Link to="/" className="font-medium transition hover:text-[#3A68C2]">
            خانه
          </Link>
          <span className="text-slate-400 opacity-80">›</span>
          <span className="font-semibold text-slate-700">راهنمای خرید</span>
        </nav>

        <motion.header
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_12px_40px_-24px_rgba(58,104,194,0.35),0_8px_28px_-20px_rgba(15,23,42,0.12)] sm:rounded-[1.35rem]"
        >
          <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#3A68C2]/12 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-12 h-48 w-48 rounded-full bg-emerald-200/30 blur-3xl" />
          <div className="relative grid gap-6 p-5 sm:p-8 lg:grid-cols-[1.2fr_0.85fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#3A68C2]/10 px-3 py-1 text-[11px] font-bold text-[#1C3B9B] sm:text-xs">
                <Sparkles className="h-3.5 w-3.5" />
                مسیر پیشنهادی خرید دوره
              </div>
              <h1 className="mt-3 text-2xl font-black leading-tight text-[#0F172A] sm:text-3xl lg:text-[2rem]">
                راهنمای خرید حرفه‌ای
              </h1>
              <p className="mt-3 max-w-2xl text-sm font-medium leading-7 text-slate-600 sm:text-[15px]">
                از انتخاب دوره تا فعال‌سازی آنی دسترسی، مرحله‌به‌مرحله مطابق فرایند واقعی سایت (سبد خرید، کیف پول، کوپن و
                درخواست افزایش موجودی) پیش می‌روید.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link
                  to="/courses"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#3A68C2] px-4 py-2.5 text-sm font-black text-white shadow-md transition hover:bg-[#345DAF]"
                >
                  شروع از دوره‌ها
                  <ArrowLeft className="h-4 w-4" />
                </Link>
                <Link
                  to="/cart"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 transition hover:border-[#3A68C2]/30 hover:bg-slate-50"
                >
                  رفتن به سبد
                  <ShoppingCart className="h-4 w-4 text-[#3A68C2]" />
                </Link>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-gradient-to-br from-slate-50 to-white p-4 sm:p-5">
              <p className="text-xs font-black text-slate-800">در یک نگاه</p>
              <ul className="mt-3 space-y-2.5 text-[13px] font-semibold leading-6 text-slate-600">
                <li className="flex gap-2">
                  <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-[#1C3B9B]" />
                  پرداخت سبد با <span className="text-slate-900">کیف پول</span> داخلی سایت
                </li>
                <li className="flex gap-2">
                  <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                  کد تخفیف فقط در <span className="text-slate-900">صفحهٔ سبد</span>
                </li>
                <li className="flex gap-2">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  بعد از تسویهٔ موفق، دسترسی دوره <span className="text-slate-900">فوری</span> فعال می‌شود
                </li>
              </ul>
            </div>
          </div>
        </motion.header>

        <motion.section
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
          initial="hidden"
          animate="visible"
          className="mt-8 sm:mt-10"
        >
          <div className="mb-4 flex items-end justify-between gap-3">
            <h2 className="text-lg font-black text-[#0F172A] sm:text-xl">مراحل خرید</h2>
            <Link
              to="/dashboard"
              className="hidden items-center gap-1 text-xs font-bold text-[#3A68C2] hover:underline sm:inline-flex"
            >
              ورود به داشبورد
              <LayoutDashboard className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="relative grid gap-3 sm:grid-cols-2 lg:grid-cols-3 lg:gap-4">
            <div
              className="pointer-events-none absolute left-[8%] right-[8%] top-[2.25rem] hidden h-px bg-gradient-to-l from-transparent via-[#3A68C2]/20 to-transparent lg:block"
              aria-hidden
            />
            {steps.map((s: Step) => {
              const Icon = s.icon;
              return (
                <motion.article
                  key={s.n}
                  variants={fadeUp}
                  className="group relative rounded-2xl border border-slate-200/90 bg-white p-4 shadow-[0_10px_32px_-22px_rgba(15,23,42,0.2)] transition hover:border-[#3A68C2]/25 hover:shadow-[0_14px_40px_-20px_rgba(58,104,194,0.18)] sm:p-5"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#3A68C2]/10 text-[#1C3B9B] ring-1 ring-[#3A68C2]/15">
                      <Icon className="h-5 w-5" strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-md bg-slate-900 px-1.5 text-[11px] font-black text-white">
                          {s.n.toLocaleString('fa-IR')}
                        </span>
                        <h3 className="text-sm font-black text-[#0F172A] sm:text-[15px]">{s.title}</h3>
                      </div>
                      <p className="mt-2 text-[12px] font-medium leading-6 text-slate-600 sm:text-[13px]">{s.desc}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Link
                          to={s.anchor}
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-[#3A68C2] transition group-hover:gap-1.5"
                        >
                          {s.anchorLabel}
                          <ArrowLeft className="h-3.5 w-3.5" />
                        </Link>
                        {'anchorSecondary' in s && s.anchorSecondary ? (
                          <Link
                            to={s.anchorSecondary.to}
                            className="text-[11px] font-semibold text-slate-500 hover:text-[#3A68C2]"
                          >
                            {s.anchorSecondary.label}
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </motion.section>

        <section className="mt-10 grid gap-4 lg:grid-cols-2 lg:gap-5">
          <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50/90 to-white p-5 shadow-sm sm:p-6">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-[#1C3B9B]" />
              <h2 className="text-base font-black text-[#0F172A] sm:text-lg">نکات مهم امنیتی و پشتیبانی</h2>
            </div>
            <ul className="mt-4 space-y-2.5 text-[13px] font-medium leading-7 text-slate-700">
              <li>اطلاعات ورود به حساب را در اختیار دیگران قرار ندهید.</li>
              <li>شماره پیگیری و رسید واریز را هنگام افزایش موجودی دقیق ثبت کنید تا تأیید سریع‌تر انجام شود.</li>
              <li>در صورت بروز خطا، متن پیام سیستم را یادداشت کنید و با پشتیبانی در میان بگذارید.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-center gap-2">
              <CircleHelp className="h-5 w-5 text-slate-600" />
              <h2 className="text-base font-black text-[#0F172A] sm:text-lg">سوالات پرتکرار</h2>
            </div>
            <div className="mt-4 divide-y divide-slate-100">
              {faqItems.map((item, i) => {
                const open = openFaq === i;
                return (
                  <div key={item.q} className="py-1">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="flex w-full items-center justify-between gap-3 py-2.5 text-right text-[13px] font-bold text-slate-800 transition hover:text-[#1C3B9B]"
                    >
                      {item.q}
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {open ? (
                      <p className="pb-3 pr-1 text-[12px] font-medium leading-7 text-slate-600 sm:text-[13px]">{item.a}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <div className="mt-10 rounded-2xl border border-[#1C3B9B]/20 bg-[#1C3B9B] px-5 py-6 text-center text-white shadow-lg sm:px-8">
          <p className="text-lg font-black sm:text-xl">هنوز مطمئن نیستید از کجا شروع کنید؟</p>
          <p className="mx-auto mt-2 max-w-lg text-sm font-medium leading-7 text-white/90">
            از صفحهٔ دوره‌ها یک عنوان را باز کنید، به سبد اضافه کنید و در سبد موجودی و کوپن را بررسی کنید.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2.5 text-sm font-black text-[#1C3B9B] transition hover:bg-slate-100"
            >
              تماس با پشتیبانی
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <Link
              to="/terms"
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/40 bg-transparent px-4 py-2.5 text-sm font-bold text-white transition hover:bg-white/10"
            >
              قوانین و مقررات
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseGuide;
